/**
 * chat_patch_to_form_adapter.ts
 *
 * Extracts <FIELD_UPDATE> blocks from raw AI response text and converts
 * them to FieldPatch[]. Dispatches sf:highlight-field CustomEvents so
 * TechPackForm.tsx HighlightCtx shows yellow border feedback.
 *
 * Phase 6 will wire extractFieldPatches() into App.tsx sendToAI() BEFORE
 * the display-strip regex, so tags are parsed before being removed from
 * the chat UI.
 *
 * Supported <FIELD_UPDATE> content formats:
 *   JSON:       {"metadata.product_category": "Jacket", "measurements.D_chest": "96"}
 *   Line-based: metadata.product_category: Jacket
 *               measurements.D_chest = 96
 */

export interface FieldPatch {
  fieldPath:      string;  // e.g. "metadata.product_category", "measurements.D_chest"
  suggestedValue: string;
  confidence?:    number;
  source:         "ai_extraction";
}

const FIELD_UPDATE_RE = /<FIELD_UPDATE>([\s\S]*?)<\/FIELD_UPDATE>/g;

/**
 * Extract all FieldPatch objects from a raw AI response string.
 * Processes every <FIELD_UPDATE>...</FIELD_UPDATE> block found.
 */
export function extractFieldPatches(rawResponse: string): FieldPatch[] {
  const patches: FieldPatch[] = [];
  FIELD_UPDATE_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = FIELD_UPDATE_RE.exec(rawResponse)) !== null) {
    patches.push(...parseFieldUpdateBlock(match[1].trim()));
  }
  return patches;
}

function parseFieldUpdateBlock(content: string): FieldPatch[] {
  // JSON format (preferred — more reliable from structured models)
  if (content.startsWith("{")) {
    try {
      const obj = JSON.parse(content) as Record<string, unknown>;
      return Object.entries(obj)
        .filter(([, v]) => typeof v === "string" || typeof v === "number")
        .map(([fieldPath, v]) => ({
          fieldPath,
          suggestedValue: String(v),
          source: "ai_extraction" as const,
        }));
    } catch {
      // fall through to line-based
    }
  }

  // Line-based format: "field.path: value" or "field.path = value"
  const patches: FieldPatch[] = [];
  for (const line of content.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#") || trimmed.startsWith("//")) continue;
    const colonIdx = trimmed.indexOf(":");
    const eqIdx    = trimmed.indexOf("=");
    const sepIdx   = colonIdx !== -1 ? (eqIdx !== -1 ? Math.min(colonIdx, eqIdx) : colonIdx) : eqIdx;
    if (sepIdx === -1) continue;
    const fieldPath      = trimmed.slice(0, sepIdx).trim();
    const suggestedValue = trimmed.slice(sepIdx + 1).trim();
    if (fieldPath && suggestedValue) {
      patches.push({ fieldPath, suggestedValue, source: "ai_extraction" });
    }
  }
  return patches;
}

/**
 * Dispatch sf:highlight-field CustomEvents for a list of patches.
 * TechPackForm HighlightCtx listens for this event and shows a yellow border
 * on the corresponding input for 0.8 seconds.
 *
 * Phase 6 FieldPatchQueue calls this after the user accepts a patch.
 */
export function highlightPatches(patches: FieldPatch[]): void {
  for (const patch of patches) {
    window.dispatchEvent(
      new CustomEvent("sf:highlight-field", { detail: { field: patch.fieldPath } }),
    );
  }
}

/**
 * Convenience: extract patches AND highlight them immediately.
 * Use when there is no review step (e.g. auto-apply mode).
 */
export function extractAndHighlight(rawResponse: string): FieldPatch[] {
  const patches = extractFieldPatches(rawResponse);
  highlightPatches(patches);
  return patches;
}
